import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Header, SpaceBetween, StatusIndicator, Table } from '@cloudscape-design/components';
import { EmptyState, PageSkeleton, createApiClient, useAuth, useNotify } from '@deloitte-us-consulting/gp-sdk';
import DataCatalogAdmin from '../DataCatalogAdmin';
import type { SystemSummary } from './systems';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

const STATUS = {
  ready: { type: 'success', label: 'Ready' },
  needs_credential: { type: 'warning', label: 'Needs credential' },
  planned: { type: 'pending', label: 'Planned' },
} as const;

const refreshedLabel = (iso: string | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * The top level of the Data page: one row per external system (ADR 0031, "Data
 * systems"). Choosing a system opens its tables and fields, which is the detail
 * layer; most days an admin never needs to go that deep.
 */
export default function SystemsPanel({
  systemId,
  onOpen,
  onBack,
}: {
  systemId?: string | undefined;
  onOpen: (systemId: string) => void;
  onBack: () => void;
}) {
  const { accessToken } = useAuth();
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);
  const [systems, setSystems] = useState<SystemSummary[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<SystemSummary[]>('/api/catalog/systems')
      .then((rows) => {
        if (!cancelled) setSystems(rows);
      })
      .catch((e) => {
        if (!cancelled) notify({ type: 'error', header: 'Could not load data systems', content: (e as Error).message });
      });
    return () => {
      cancelled = true;
    };
    // Load once. Intentionally NOT depending on `notify` (a fresh function each render).
  }, [api]);

  if (systems === null) return <PageSkeleton label="Loading data systems…" />;

  const selected = systemId ? systems.find((s) => s.systemId === systemId) : undefined;
  if (systemId && !selected) {
    return (
      <EmptyState
        title="Unknown data system"
        description="That system is not one the platform knows about."
        action={<Button onClick={onBack}>Back to all systems</Button>}
      />
    );
  }

  if (selected) {
    return (
      <SpaceBetween size="l">
        <Button variant="inline-link" iconName="angle-left" onClick={onBack}>
          All data systems
        </Button>
        <Header
          variant="h2"
          description={selected.description}
          info={<StatusIndicator type={STATUS[selected.status].type}>{STATUS[selected.status].label}</StatusIndicator>}
        >
          {selected.displayName}
        </Header>
        {selected.status === 'needs_credential' && (
          <Alert type="warning" header={`No credential is stored for ${selected.displayName}`}>
            Add a read-only token to Secrets Manager (for AirTable, the secret named <Box variant="code">airtable-data-source</Box>).
            Until then, registering and refreshing tables will fail.
          </Alert>
        )}
        <DataCatalogAdmin embedded systemId={selected.systemId} />
      </SpaceBetween>
    );
  }

  return (
    <Table
      variant="container"
      header={
        <Header
          variant="h2"
          counter={`(${systems.length})`}
          description="The external systems the platform reads from. Open one for its tables and fields."
        >
          Data systems
        </Header>
      }
      items={systems}
      trackBy="systemId"
      columnDefinitions={[
        {
          id: 'system',
          header: 'System',
          isRowHeader: true,
          cell: (s) => (
            <SpaceBetween size="xxs">
              <Box variant="strong">{s.displayName}</Box>
              <Box variant="small" color="text-body-secondary">
                {s.description}
              </Box>
            </SpaceBetween>
          ),
        },
        {
          id: 'status',
          header: 'Status',
          cell: (s) => <StatusIndicator type={STATUS[s.status].type} wrapText={false}>{STATUS[s.status].label}</StatusIndicator>,
        },
        {
          id: 'tables',
          header: 'Tables',
          cell: (s) =>
            s.availability === 'planned' ? '—' : s.restrictedTables ? `${s.tables} (${s.restrictedTables} restricted)` : String(s.tables),
        },
        { id: 'apps', header: 'Apps using it', cell: (s) => (s.availability === 'planned' ? '—' : s.apps) },
        { id: 'refreshed', header: 'Tables last read', cell: (s) => refreshedLabel(s.refreshedAt) },
        {
          id: 'actions',
          header: '',
          cell: (s) =>
            s.availability === 'planned' ? null : (
              <Button variant="inline-link" wrapText={false} ariaLabel={`Manage ${s.displayName} tables`} onClick={() => onOpen(s.systemId)}>
                Manage tables
              </Button>
            ),
        },
      ]}
    />
  );
}
